import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import {
  getAllExtensionRequests,
  getMyExtensionRequests,
  approveExtensionRequest,
  rejectExtensionRequest,
} from '@/api/videoApi'
import { videoKeys } from '@/api/querry_keys'

const PAGE_SIZE = 9

// ─── Admin: all extension requests (infinite) ────────────────────────────────

interface UseAdminExtensionRequestsParams {
  filterUser?: string
  filterStatus?: string
  isAdmin: boolean
}

export const useAdminExtensionRequestsInfinite = ({
  filterUser = 'all',
  filterStatus = 'all',
  isAdmin,
}: UseAdminExtensionRequestsParams) => {
  return useInfiniteQuery({
    queryKey: videoKeys.extensionRequestsInfinite({
      userEmail: filterUser,
      status: filterStatus,
    }),
    queryFn: ({ pageParam = 1 }) =>
      getAllExtensionRequests(pageParam, PAGE_SIZE, filterUser, filterStatus),
    initialPageParam: 1,
    getNextPageParam: last =>
      last.page < last.total_pages ? last.page + 1 : undefined,
    enabled: isAdmin,
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
  })
}

// ─── User: own extension requests (infinite) ─────────────────────────────────

export const useMyExtensionRequestsInfinite = (status?: string) => {
  return useInfiniteQuery({
    queryKey: videoKeys.myExtensionRequestsInfinite({ status }),
    queryFn: ({ pageParam = 1 }) =>
      getMyExtensionRequests(pageParam, PAGE_SIZE, status),
    initialPageParam: 1,
    getNextPageParam: last =>
      last.page < last.total_pages ? last.page + 1 : undefined,
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
  })
}

// ─── Admin: approve / reject mutations ───────────────────────────────────────

export const useApproveExtensionRequest = (filterUser?: string, filterStatus?: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => approveExtensionRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: videoKeys.extensionRequestsInfinite({
          userEmail: filterUser ?? 'all',
          status: filterStatus ?? 'all',
        }),
      })
    },
  })
}

export const useRejectExtensionRequest = (filterUser?: string, filterStatus?: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, feedback }: { id: string; feedback?: string }) =>
      rejectExtensionRequest(id, feedback),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: videoKeys.extensionRequestsInfinite({
          userEmail: filterUser ?? 'all',
          status: filterStatus ?? 'all',
        }),
      })
    },
  })
}
